/**
 * Model registry for plugins.
 *
 * Single source of truth for all LLM model IDs, providers, and pricing.
 * Plugins use this to access model metadata without importing from the server.
 */

import type { ChatModel, ChatProvider } from '@reflex/shared';
import {
  RATE_CARD,
  type PublishedModelRate,
  type PublishedRate,
  type RateCard,
  type RateCardProvider,
} from './rate-card.js';

export type ProviderName = ChatProvider;

export interface ModelPricing {
  input: number;
  output: number;
  cacheCreation: number;
  cacheRead: number;
}

export interface ModelDef {
  provider: ProviderName;
  modelId: string;
  pricing: ModelPricing;
  /**
   * Provenance: the rate-card row this entry's pricing was derived from,
   * carried verbatim (card key included, which may differ from `modelId` when
   * the registry pins a dated build id). `pricing` is lossy by design —
   * {@link toRegistryPricing} renders `null` cache rates as `0` — so
   * consumers that need the card's `null`/non-`null` semantics (the costs
   * plugin's `computeUsd`, which bills `null` cache tokens at the input rate)
   * must read this instead of un-converting `pricing`.
   *
   * Absent only for entries with no card row at all: dedicated deployments
   * billed per GPU (the GLM entries), whose all-zero `pricing` is literal.
   */
  card?: PublishedModelRate;
}

/**
 * Registry pricing semantics for a published rate: `null` cache rates render
 * as `0` ("not separately charged"). The costs plugin derives its seed table
 * from the same card with its own semantics (`null` bills at the input rate);
 * see `rate-card.ts` for the distinction.
 */
function toRegistryPricing(rate: PublishedRate): ModelPricing {
  return {
    input: rate.inputUsdPerMtok,
    output: rate.outputUsdPerMtok,
    cacheCreation: rate.cacheWriteUsdPerMtok ?? 0,
    cacheRead: rate.cacheReadUsdPerMtok ?? 0,
  };
}

/**
 * Build a registry entry whose pricing comes from the shared {@link RATE_CARD}
 * — the card key is compile-checked, so a typo'd or uncarded model id fails
 * `tsc`, not billing. `opts.modelId` pins a dated build id (e.g.
 * `claude-haiku-4-5-20251001`) whose price is the card's undated entry.
 */
function fromCard<P extends RateCardProvider, M extends keyof RateCard[P] & string>(
  provider: P,
  model: M,
  opts?: { modelId?: string },
): ModelDef {
  const rates = RATE_CARD[provider] as Record<string, PublishedRate>;
  const rate = rates[model];
  return {
    provider,
    modelId: opts?.modelId ?? model,
    pricing: toRegistryPricing(rate),
    // Keep the card row itself, keyed by its card key rather than `modelId`:
    // the two diverge whenever `opts.modelId` pins a dated build id.
    card: { provider, model, ...rate },
  };
}

/**
 * Single source of truth for all LLM model IDs and providers.
 *
 * When a provider renames or deprecates a model, update the `modelId` here —
 * every consumer (chat routes, flows adapter, classification service, usage
 * tracking, etc.) picks up the change automatically.
 *
 * Pricing (USD per million tokens) is derived from the shared rate card in
 * `rate-card.ts` — edit prices there, never inline here. The only literal
 * pricing allowed below is the all-zero "no per-token rate" of dedicated
 * deployments.
 */
export const MODEL_REGISTRY: Record<ChatModel | 'sonnet-5', ModelDef> = {
  sonnet: fromCard('anthropic', 'claude-sonnet-5-5'),
  'sonnet-5': fromCard('anthropic', 'claude-sonnet-5'),
  // Superseded by `sonnet`, kept so runs recorded against Sonnet 4.6 still
  // price. `tryGetModelDef` matches on model id, and dropping the entry would
  // leave every historical 4.6 row unpriced rather than mispriced.
  'sonnet-4-6': fromCard('anthropic', 'claude-sonnet-4-6'),
  opus: fromCard('anthropic', 'claude-opus-5-5'),
  // Superseded by `opus`, kept so runs recorded against Opus 5 still price
  // (same pattern as `sonnet-4-6` above).
  'opus-5': fromCard('anthropic', 'claude-opus-5'),
  haiku: fromCard('anthropic', 'claude-haiku-4-5', { modelId: 'claude-haiku-4-5-20251001' }),
  fable: fromCard('anthropic', 'claude-fable-5-1'),
  // Superseded by `fable`, kept so runs recorded against Fable 5 still price
  // (same pattern as `sonnet-4-6` above).
  'fable-5': fromCard('anthropic', 'claude-fable-5'),
  'gpt-6-astra': fromCard('openai', 'gpt-6-astra'),
  'gpt-6.1-sol': fromCard('openai', 'gpt-6.1-sol'),
  'gpt-5.6-sol': fromCard('openai', 'gpt-5.6-sol'),
  'gpt-5.6-terra': fromCard('openai', 'gpt-5.6-terra'),
  'gpt-5.6-luna': fromCard('openai', 'gpt-5.6-luna'),
  'gpt-5.5': fromCard('openai', 'gpt-5.5'),
  'gpt-5.4-mini': fromCard('openai', 'gpt-5.4-mini'),
  'gpt-5.5-pro': fromCard('openai', 'gpt-5.5-pro'),
  'gemini-2.5-pro': fromCard('google', 'gemini-2.5-pro'),
  'gemini-2.5-flash': fromCard('google', 'gemini-2.5-flash'),
  'grok-4.20': fromCard('xai', 'grok-4.20-0309-non-reasoning'),
  'grok-4.1-fast': fromCard('xai', 'grok-4-1-fast-non-reasoning'),
  'glm-5.2': {
    provider: 'nebius',
    // Runloop's dedicated GLM-5.2 deployment on the Nebius Token Factory,
    // addressed by its routing key. Billed per-GPU on the dedicated endpoint,
    // not per token, so token pricing is zeroed (usage rows stay unpriced).
    modelId: 'dedicated/runloop/GLM-5.2-NVFP4-39V6Isdn6nET',
    pricing: { input: 0, output: 0, cacheCreation: 0, cacheRead: 0 },
  },
  'glm-5.2-runloop': {
    provider: 'runloop',
    modelId: 'dedicated/runloop/GLM-5.2-NVFP4-39V6Isdn6nET',
    pricing: { input: 0, output: 0, cacheCreation: 0, cacheRead: 0 },
  },
};

export const PROVIDER_SECRET_KEYS: Record<ProviderName, string> = {
  anthropic: 'ANTHROPIC_API_KEY',
  openai: 'OPENAI_API_KEY',
  google: 'GOOGLE_GENERATIVE_AI_API_KEY',
  xai: 'XAI_API_KEY',
  nebius: 'NEBIUS_API_KEY',
  runloop: 'RUNLOOP_MODELS_API_KEY',
};

/**
 * Named defaults by purpose. Change these when swapping the model a
 * subsystem should use — no need to touch consumer code.
 */
export const DEFAULT_MODELS = {
  chat: 'sonnet' as ChatModel,
  flowLlm: 'sonnet' as ChatModel,
  classification: 'haiku' as ChatModel,
} as const;

export function getModelId(model: ChatModel): string {
  return MODEL_REGISTRY[model].modelId;
}

export function getProvider(model: ChatModel): ProviderName {
  return MODEL_REGISTRY[model].provider;
}

/**
 * Characters that may abut a model id without being part of it: the
 * separators vendors use to prefix, suffix and qualify ids. `[` and `]`
 * are included for Anthropic's context-window suffix (`claude-opus-5[1m]`),
 * and whitespace/`@`/`,` for ids embedded in a longer log line.
 */
const ID_BOUNDARY_CHARS = /[/\-.:_@,[\]\s]/;

function isBoundary(char: string): boolean {
  return char === '' || ID_BOUNDARY_CHARS.test(char);
}

/**
 * True when `modelId` sits at an id boundary inside `query` — i.e. it is
 * not spliced into the middle of a longer token. So `anthropic/claude-sonnet-4-6`,
 * `claude-sonnet-4-6-20260115` and `claude-opus-5[1m]` all contain their
 * id at a boundary, while a hypothetical `xclaude-sonnet-4-6y` does not.
 */
function containsAtBoundary(query: string, modelId: string): boolean {
  // An empty id is contained in everything and would never advance the
  // scan below — no registry entry has one, and this keeps it that way.
  if (modelId === '') return false;
  let from = 0;
  for (;;) {
    const at = query.indexOf(modelId, from);
    if (at === -1) return false;
    const before = at === 0 ? '' : query[at - 1]!;
    const afterIdx = at + modelId.length;
    const after = afterIdx >= query.length ? '' : query[afterIdx]!;
    if (isBoundary(before) && isBoundary(after)) return true;
    from = at + 1;
  }
}

/**
 * Collapse the candidates a matching pass found into a single answer.
 *
 * Two registry entries can carry the same `modelId` — the dedicated GLM
 * deployment is reachable under both `nebius` and `runloop` — so a match
 * on that id alone cannot say which provider the caller meant. Candidates
 * that agree on provider and pricing are one answer; candidates that
 * disagree are unresolvable, and saying so beats letting registry
 * iteration order pick a provider.
 */
function collapseCandidates(candidates: readonly ModelDef[]): ModelDef | null {
  const first = candidates[0];
  if (!first) return null;
  const agrees = (def: ModelDef): boolean =>
    def.provider === first.provider &&
    def.modelId === first.modelId &&
    def.pricing.input === first.pricing.input &&
    def.pricing.output === first.pricing.output &&
    def.pricing.cacheRead === first.pricing.cacheRead &&
    def.pricing.cacheCreation === first.pricing.cacheCreation;
  return candidates.every(agrees) ? first : null;
}

/**
 * Resolve the registry definition for a raw model string without a
 * default. Returns `null` when nothing matches, or when the string is
 * too ambiguous to resolve safely. Exposes the full {@link ModelDef}
 * (including `provider`) so callers can verify the matched model
 * actually belongs to an expected provider before trusting its pricing —
 * preventing an unknown `(provider, model)` pair from silently borrowing
 * another provider's model rates.
 *
 * Resolution order, most-specific first:
 *
 * 1. Exact match on the friendly name (always unique) or the `modelId`,
 *    both compared case-insensitively. `modelId` is lowercased on *both*
 *    sides: the dedicated GLM deployments carry a mixed-case routing key,
 *    and comparing a lowercased query against the raw id meant those
 *    entries could never resolve by their own model id.
 * 2. The query *contains* a `modelId` at an id boundary — the shape a
 *    vendor-prefixed (`anthropic/claude-sonnet-4-6`), dated-build
 *    (`claude-sonnet-4-6-20260115`) or context-qualified
 *    (`claude-opus-5[1m]`) id takes. The **longest** matching `modelId`
 *    wins, so a more specific entry is never shadowed by a shorter one
 *    that happens to be a prefix of it.
 * 3. The query sits at an id boundary *inside* a `modelId` — the reverse
 *    of pass 2, for a truncated query (`claude-sonnet-4` for
 *    `claude-sonnet-4-6`). Boundary-checked the same way as pass 2, so a
 *    query that only accidentally lands inside a longer id
 *    (`onnet-4` inside `claude-sonnet-4-6`) does not match.
 *
 * Every pass resolves only when its candidates agree on provider and
 * pricing (see {@link collapseCandidates}); otherwise the answer is
 * `null`. A truncation spanning several entries is genuinely ambiguous —
 * `gpt-5` is a prefix of six OpenAI ids priced from $0.75 to $30 per
 * MTok — and registry iteration order is not a pricing decision.
 * Returning `null` leaves the caller's own fallback chain (seed card,
 * tokenlens catalog) to answer, and an unpriced row is recoverable in a
 * way a confidently wrong one is not.
 *
 * A blank query resolves to `null` rather than matching every entry.
 */
export function tryGetModelDef(modelString: string): ModelDef | null {
  const lower = modelString.trim().toLowerCase();
  if (lower === '') return null;

  const entries = Object.entries(MODEL_REGISTRY).map(
    ([friendlyName, def]) => [friendlyName, def, def.modelId.toLowerCase()] as const,
  );

  const exact = entries.filter(
    ([friendlyName, , modelId]) => lower === friendlyName || lower === modelId,
  );
  // A friendly-name hit is unique by construction (object keys), so it
  // answers on its own even when it shares a modelId with a sibling entry.
  const friendlyHit = exact.find(([friendlyName]) => lower === friendlyName);
  if (friendlyHit) return friendlyHit[1];
  if (exact.length > 0) return collapseCandidates(exact.map(([, def]) => def));

  const contained = entries.filter(([, , modelId]) => containsAtBoundary(lower, modelId));
  if (contained.length > 0) {
    const longest = Math.max(...contained.map(([, , modelId]) => modelId.length));
    return collapseCandidates(
      contained.filter(([, , modelId]) => modelId.length === longest).map(([, def]) => def),
    );
  }

  const truncations = entries.filter(([, , modelId]) => containsAtBoundary(modelId, lower));
  return collapseCandidates(truncations.map(([, def]) => def));
}

/**
 * Resolve pricing for a raw model string without a default. Resolves via
 * {@link tryGetModelDef} and returns `null` when nothing matches or the
 * string is too ambiguous to resolve — so callers that must not mis-price
 * an unknown model (e.g. the cost recorder) can leave the row unpriced
 * instead of silently charging sonnet rates.
 */
export function tryGetPricing(modelString: string): ModelPricing | null {
  return tryGetModelDef(modelString)?.pricing ?? null;
}

/**
 * Resolve pricing for a raw model string (e.g. from JSONL usage logs).
 * Resolves via {@link tryGetModelDef} and falls back to sonnet pricing.
 * Prefer {@link tryGetPricing} anywhere a wrong price is worse than no
 * price — this one charges sonnet rates for an unknown model.
 */
export function getPricing(modelString: string): ModelPricing {
  return tryGetPricing(modelString) ?? MODEL_REGISTRY.sonnet.pricing;
}
